import { type ChangeEvent, memo } from "react";
import MultiDatePicker from "react-multi-date-picker";
import { useTranslation } from "react-i18next";
import gregorian from "react-date-object/calendars/gregorian";
import persianFa from "react-date-object/locales/persian_fa";

import { persian } from "./calendar";
import gregorianAr from "./gregorianAr";
import type { IDatePickerProps } from "./types";

import type { ILocaleType } from "@configs";
import { Input } from "@components/formControls/input";
import { useReadOnly } from "@/hooks";

const DATE_DIGITS_PATTERN = /[0-9۰-۹٠-٩]/gu;

function formatDateInput(
  inputValue: string,
  onlyYear: boolean,
  isDeleting: boolean,
): string {
  const maximumLength = onlyYear ? 4 : 8;

  const digits = (inputValue.match(DATE_DIGITS_PATTERN) ?? [])
    .join("")
    .slice(0, maximumLength);

  if (onlyYear || digits.length < 4) {
    return digits;
  }

  const year = digits.slice(0, 4);
  const month = digits.slice(4, 6);
  const day = digits.slice(6, 8);

  let formattedValue = year;

  /*
   * Add the first slash immediately after the fourth digit.
   * Do not re-add it when the user is pressing Backspace.
   */
  if (digits.length > 4 || (digits.length === 4 && !isDeleting)) {
    formattedValue += `/${month}`;
  }

  /*
   * Add the second slash immediately after the sixth digit.
   * Do not re-add it when the user is pressing Backspace.
   */
  if (digits.length > 6 || (digits.length === 6 && !isDeleting)) {
    formattedValue += `/${day}`;
  }

  return formattedValue;
}

export const DatePicker = memo(function DatePicker(props: IDatePickerProps) {
  const {
    value: _value,
    onChangeValue,
    readOnly: propsReadOnly,
    disabled,
    date,
    placeholder,
    minDate,
    onlyYear = false,
    ...rest
  } = props;

  const readOnly = useReadOnly(propsReadOnly);
  const { i18n } = useTranslation();
  const lng = i18n.language as ILocaleType;

  let calendar;
  let locale;

  if (lng === "fa") {
    calendar = persian;
    locale = persianFa;
  } else if (lng === "ar") {
    calendar = gregorian;
    locale = gregorianAr;
  }

  const resolvedPlaceholder =
    placeholder ||
    new Date()
      .toLocaleDateString(
        lng === "fa" ? "fa" : "en-CA",
        onlyYear ? { year: "numeric" } : undefined,
      )
      .replaceAll("-", "/");

  return (
    <MultiDatePicker
      render={(inputValue, openCalendar, handleValueChange) => (
        <Input
          {...rest}
          dir={"ltr"}
          value={inputValue}
          placeholder={resolvedPlaceholder}
          inputMode={"numeric"}
          maxLength={onlyYear ? 4 : 10}
          readOnly={readOnly}
          disabled={disabled}
          onFocus={openCalendar}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            const inputType = (event.nativeEvent as InputEvent).inputType;
            const isDeleting = inputType?.startsWith("delete") ?? false;

            event.currentTarget.value = formatDateInput(
              event.currentTarget.value,
              onlyYear,
              isDeleting,
            );

            handleValueChange(event);
          }}
        />
      )}
      value={date}
      format={onlyYear ? "YYYY" : "YYYY/MM/DD"}
      onChange={(selectedDate) => {
        if (!selectedDate) {
          return;
        }

        onChangeValue?.(selectedDate.toDate().toISOString());
      }}
      onlyYearPicker={onlyYear}
      calendar={calendar}
      locale={locale}
      readOnly={readOnly}
      disabled={disabled}
      portal
      minDate={minDate}
    />
  );
});
